import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import classNames from 'classnames';
import { FormEvent, KeyboardEvent, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useBrandConfig } from '~/config/branding';
import './ChatbotView.css';
import { usePIIValidation } from './hooks/usePIIValidation';
import { ChatbotContext } from './index';

// MCV-1788: the greeting is built here, from the brand, rather than sent by the server, so
// HealthcareLX users are not greeted as MedCredVault users.
const greetingFor = (siteName: string) =>
  `Hi! I'm your ${siteName} AI Chat assistant. \n` +
  'I can help you with CME/CE requirements, medical licensing, credentialing, and more. What can I help you with today?';

export const ChatbotView = () => {
  const chatbotRef = ChatbotContext.useActorRef();
  const { validateQuery } = usePIIValidation();
  const { siteName } = useBrandConfig();

  const { messages, inputValue, isLoading, isTyping } = useSelector(chatbotRef, (state) => {
    return state.context;
  });

  // Track connection state for UI
  const isConnecting = useSelector(chatbotRef, (state) => state.matches('$_CONNECTING'));

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mountCountRef = useRef(0);
  const greetedRef = useRef(false);

  useEffect(() => {
    mountCountRef.current += 1;
    chatbotRef.send({ type: 'ON_CONNECT' });

    // Greets once the socket is connected. Reads the current snapshot rather than a snapshot
    // handed in by the subscription, because the subscription alone can miss the connection:
    // the panel may have been closed while the socket was still opening ($_CONNECTING ignores
    // ON_DISCONNECT), so the actor reaches $_CONNECTED while this view is unmounted. On reopen
    // ON_CONNECT is ignored in $_CONNECTED and xstate's subscribe does not replay the current
    // snapshot, so no transition would ever arrive to greet on.
    const greetIfConnected = () => {
      const state = chatbotRef.getSnapshot();
      if (state.matches('$_CONNECTED') && state.context.socket && !greetedRef.current) {
        console.debug('WebSocket connected, showing greeting');
        // Set before sending: the send is processed synchronously when it is not made from
        // inside a subscriber, and that notifies this subscription again before it returns.
        greetedRef.current = true;
        chatbotRef.send({
          type: 'ON_RECEIVE_MESSAGE',
          payload: { message: greetingFor(siteName) },
        });
      }
    };

    // Subscribe to state changes to detect when WebSocket is connected
    const subscription = chatbotRef.subscribe(greetIfConnected);
    // And once now, for a connection that was already made before this view subscribed.
    greetIfConnected();

    // The events are dispatched on `document`, shared by every account that has used the
    // page. Only the socket this chat holds may speak to it.
    const isOwnSocket = (event: CustomEvent) =>
      !event.detail?.socket || event.detail.socket === chatbotRef.getSnapshot().context.socket;

    // Handle KB messages
    const handleWebSocketMessage = (event: CustomEvent) => {
      if (!isOwnSocket(event)) return;
      chatbotRef.send({
        type: 'ON_RECEIVE_MESSAGE',
        payload: {
          message: event.detail.message,
          citations: event.detail.citations,
          suggestedTopics: event.detail.suggestedTopics,
          isClarification: event.detail.isClarification,
          isPii: event.detail.isPii,
          isRateLimited: event.detail.isRateLimited,
          sessionId: event.detail.sessionId,
        },
      });
    };

    // Handle typing indicator
    const handleTyping = (event: CustomEvent) => {
      if (!isOwnSocket(event)) return;
      chatbotRef.send({
        type: 'ON_TYPING_STATUS',
        payload: { status: event.detail.status },
      });
    };

    // Handle post-reset session id update from server. Uses a shared-context
    // event so we only touch sessionId without adding a dedicated reducer.
    const handleSessionUpdate = (event: CustomEvent) => {
      if (!isOwnSocket(event)) return;
      if (event.detail.sessionId) {
        chatbotRef.send({
          type: 'ON_SEND_SHARED_CONTEXT',
          payload: { values: { sessionId: event.detail.sessionId } },
        });
      }
    };

    document.addEventListener('chatbot-message', handleWebSocketMessage as EventListener);
    document.addEventListener('chatbot-typing', handleTyping as EventListener);
    document.addEventListener('chatbot-session-update', handleSessionUpdate as EventListener);

    return () => {
      subscription.unsubscribe();
      chatbotRef.send({ type: 'ON_DISCONNECT' });
      document.removeEventListener('chatbot-message', handleWebSocketMessage as EventListener);
      document.removeEventListener('chatbot-typing', handleTyping as EventListener);
      document.removeEventListener('chatbot-session-update', handleSessionUpdate as EventListener);
    };
  }, []);

  // Existing scroll effect
  useEffect(() => {
    console.debug('Messages updated in component:', messages);
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!inputValue.trim()) return;

    // Tier 1: Client-side PII validation (FREE, instant)
    const validation = validateQuery(inputValue);
    if (!validation.isValid) {
      // Show PII error message locally
      chatbotRef.send({
        type: 'ON_RECEIVE_MESSAGE',
        payload: {
          message: validation.reason || 'Please remove personal information from your query.',
          citations: [],
          suggestedTopics: [
            'MedCredVault Overview',
            'Medical Licensing',
            'Provider Credentialing',
            'CME Requirements',
            'Document Management',
          ],
          isClarification: true,
          isPii: true,
        },
      });
      // Clear input
      chatbotRef.send({
        type: 'ON_INPUT_CHANGE',
        payload: { value: '' },
      });
      return;
    }

    // If validation passes, send to backend
    chatbotRef.send({
      type: 'ON_SEND_MESSAGE',
      payload: { message: inputValue },
    });
  };

  const handleInputChange = (value: string) => {
    chatbotRef.send({
      type: 'ON_INPUT_CHANGE',
      payload: { value },
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleReset = () => {
    chatbotRef.send({ type: 'ON_RESET_CONVERSATION' });

    // Clear backend conversation context via WebSocket.
    // Type must be 'chatbot.reset' to match the server's routing
    // (websocket_router.py splits on '.' and requires category 'chatbot').
    const socket = chatbotRef.getSnapshot().context.socket;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'chatbot.reset' }));
    }

    setTimeout(() => {
      chatbotRef.send({
        type: 'ON_RECEIVE_MESSAGE',
        payload: { message: greetingFor(siteName) },
      });
    }, 100);
  };

  // Handle topic click
  const handleTopicClick = (topic: string) => {
    chatbotRef.send({
      type: 'ON_SEND_MESSAGE',
      payload: { message: topic },
    });
  };

  // Render suggested topics as clickable buttons
  const renderSuggestedTopics = (topics?: string[]) => {
    if (!topics || topics.length === 0) return null;

    return (
      <div className="mt-3 pt-3 border-t border-grey-900">
        <p className="text-xs text-grey-400 mb-2">Suggested topics:</p>
        <div className="flex flex-wrap gap-2">
          {topics.map((topic, idx) => (
            <button
              key={idx}
              onClick={() => handleTopicClick(topic)}
              className="px-3 py-1.5 text-sm bg-primary-t-900 text-primary-300 rounded-pill hover:bg-primary-t-800 transition-colors border border-primary-t-700"
            >
              {topic}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    return messages.map((message, index) => {
      const messageKey = `${message.isUser ? 'user' : 'bot'}-${index}-${message.timestamp.getTime()}`;
      return (
        <div
          key={messageKey}
          className={classNames(
            'max-w-[85%] p-4 mb-3 shadow-sm',
            message.isUser
              ? 'bg-primary-500 text-white rounded-s-xl rounded-se-xl text-left w-fit leading-relaxed ml-auto'
              : 'bg-white text-grey-100 rounded-e-xl rounded-es-xl text-base text-left w-fit leading-relaxed border border-grey-900'
          )}
          data-cy={message.isUser ? CT.chatbot.userMessage + index : CT.chatbot.botMessage + index}
        >
          {/* User message */}
          {message.isUser && (
            <div className="break-words markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
            </div>
          )}

          {/* Bot message with PII - show banner with clarifying_question */}
          {!message.isUser && message.isPii && message.text && (
            <div className="p-3 bg-warning-subtle border border-warning-200 rounded-lg">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-warning-900 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {/*
                  Copy uses the neutral ramp, not `warning-*`. When this was written the
                  warning ramp bottomed out at #b9561b (900), lighter than the amber-800/900
                  it replaced, so amber-on-tint would have dropped this privacy copy from
                  6.8:1 to 4.2:1 — under AA. grey-200/300 restores 10.2:1 / 6.9:1.

                  `--warning-950` (#8f5417) has since been added and would clear AA here at
                  5.42:1, so amber copy is now *possible* — it is still not preferred. Grey
                  is materially higher contrast on a tint this pale, and the banner keeps
                  its warning identity through the tint, border and icon. The icon above is
                  warning-900 at 4.24:1, which is fine: as a graphical object it is held to
                  the 3:1 non-text bar, not 4.5:1.
                */}
                <div className="flex-1">
                  <p className="text-sm font-medium text-grey-200">Privacy Notice</p>
                  <p className="text-sm text-grey-300 mt-1">{message.text}</p>
                </div>
              </div>
            </div>
          )}

          {/* Bot message without PII - show regular text */}
          {!message.isUser && !message.isPii && (
            <div className="break-words markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
            </div>
          )}

          {!message.isUser &&
            message.isClarification &&
            renderSuggestedTopics(message.suggestedTopics)}
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Connection Status Banner */}
      {isConnecting && (
        <div className="bg-info-subtle border-b border-primary-t-800 px-4 py-2">
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              <span
                className="w-2 h-2 bg-primary-t-200 rounded-pill animate-bounce"
                style={{ animationDelay: '0ms' }}
              ></span>
              <span
                className="w-2 h-2 bg-primary-t-200 rounded-pill animate-bounce"
                style={{ animationDelay: '150ms' }}
              ></span>
              <span
                className="w-2 h-2 bg-primary-t-200 rounded-pill animate-bounce"
                style={{ animationDelay: '300ms' }}
              ></span>
            </div>
            <span className="text-sm text-primary-100">Connecting to chat service...</span>
          </div>
        </div>
      )}

      <div className="flex-1 p-4 overflow-y-auto bg-base min-h-0">
        {renderMessages()}
        {(isLoading || isTyping) && (
          <div className="flex items-center gap-3 max-w-[85%] p-4 mb-3 bg-white rounded-2xl rounded-bl-sm border border-grey-900 shadow-sm">
            <div className="flex gap-1 items-end h-6">
              <span
                className="w-2 h-2 bg-primary-500 rounded-pill animate-bounce"
                style={{ animationDelay: '0ms', animationDuration: '1s' }}
              ></span>
              <span
                className="w-2 h-2 bg-primary-500 rounded-pill animate-bounce"
                style={{ animationDelay: '200ms', animationDuration: '1s' }}
              ></span>
              <span
                className="w-2 h-2 bg-primary-500 rounded-pill animate-bounce"
                style={{ animationDelay: '400ms', animationDuration: '1s' }}
              ></span>
            </div>
            <span className="text-grey-300 text-sm font-medium">
              {isTyping ? 'Typing...' : 'Thinking...'}
            </span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="flex-shrink-0 border-t border-grey-300 bg-white">
        <form onSubmit={handleSubmit} className="p-4">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isConnecting
                  ? 'Connecting...'
                  : isTyping
                    ? 'AI is typing...'
                    : 'Type your message...'
              }
              disabled={isConnecting || isTyping}
              className="flex-1 border border-grey-300 rounded-control px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-black disabled:bg-base disabled:cursor-not-allowed"
              data-cy={CT.chatbot.input}
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading || isConnecting || isTyping}
              className={classNames(
                'p-3 rounded-lg transition-all',
                inputValue.trim() && !isLoading && !isConnecting && !isTyping
                  ? 'bg-primary-500 text-white hover:bg-primary-600 shadow-sm'
                  : 'bg-grey-900 text-grey-400 cursor-not-allowed'
              )}
              data-cy={CT.chatbot.sendBtn}
            >
              <PaperAirplaneIcon className="size-5" />
            </button>
          </div>
        </form>
        {messages.some((msg) => msg.isUser) && (
          <div className="px-4 pb-3 pt-0">
            <button
              onClick={handleReset}
              className="text-sm text-primary-500 hover:underline"
              data-cy={CT.chatbot.resetBtn}
            >
              Reset conversation
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
